import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import {
  CheckCircle2,
  ChevronDown,
  CircleAlert,
  Globe,
  Info,
  Mail,
  XCircle,
} from "lucide-react";
/* eslint-disable @typescript-eslint/no-explicit-any */
import StyledTooltip from "@/components/styled-tooltip";
import { Badge } from "@/components/ui/badge";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { cn } from "@/lib/utils";
import MoreInfoButton from "./more-info-button";
import React from "react";

type BlackListData = {
  rblName: string;
  listed: boolean;
  description?: string;
  response: string;
  aLookuptTimeout?: boolean;
  hostname?: string;
};

type ServerData = {
  server: string;
  blacklists: Array<BlackListData>;
};

type BlackListProps = {
  type: keyof typeof BLACKLIST_CONFIG;
  data?: ServerData[];
};

const BLACKLIST_CONFIG = {
  domainBlacklist: {
    title: "Domain Blacklists",
    description: "Domain reputation and DNS-based blacklists",
    tooltip:
      "Domain blacklists check if your domain is listed on spam databases. Being blacklisted can severely impact email deliverability, causing your messages to be blocked or sent to spam folders.",
    icon: <Globe className="w-4 h-4 text-blue-500" />,
    moreInfoSlug: "domain-blacklist",
  },
  mxServerBlacklist: {
    title: "MX Server Blacklists",
    description: "Mail server IP reputation checks",
    tooltip:
      "MX Server Blacklists check if your mail server IPs are listed on spam databases. Being blacklisted can severely impact email deliverability, as many receiving servers will reject or filter emails from blacklisted IPs.",
    icon: <Globe className="w-4 h-4 text-blue-500" />,
    moreInfoSlug: "mx-server-blacklist",
  },
} as const;

const isIPAddress = (server: string) => {
  // Simple regex to check if the server is an IP address
  return /^\d+\.\d+\.\d+\.\d+$/.test(server);
};

const sortBlacklists = (blacklists: BlackListData[]): BlackListData[] => {
  return [...blacklists].sort((a, b) => {
    // Listed entries come first
    if (a.listed && !b.listed) return -1;
    if (!a.listed && b.listed) return 1;

    // Then entries with responses
    if (a.response && !b.response) return -1;
    if (!a.response && b.response) return 1;

    // Otherwise maintain original order
    return 0;
  });
};

const getListedCount = (blacklists: BlackListData[]): number => {
  return blacklists.filter((bl) => bl.listed).length;
};

const isYellowListed = (
  blacklist: Pick<BlackListData, "rblName" | "listed" | "response">
) =>
  blacklist.listed && blacklist.response?.toLowerCase().includes("yellow list");

// Memoized components
const BlackListHeader = React.memo(
  ({
    config,
  }: {
    config: (typeof BLACKLIST_CONFIG)[keyof typeof BLACKLIST_CONFIG];
  }) => (
    <CardHeader className="p-4 hover:bg-muted/50 transition-colors">
      <div className="flex items-center justify-between">
        <CardTitle className="text-sm font-medium flex items-center gap-2">
          {config.icon}
          {config.title}
          <StyledTooltip description={config.tooltip}>
            <Info className="w-3 h-3 text-muted-foreground" />
          </StyledTooltip>
        </CardTitle>

        <div className="flex items-center gap-3">
          <MoreInfoButton slug={config.moreInfoSlug} />
          <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform duration-200 group-[data-state=open]>svg]:rotate-180" />
        </div>
      </div>
      <CardDescription className="self-start">
        {config.description}
      </CardDescription>
    </CardHeader>
  )
);

const ServerItem = React.memo(
  ({
    serverData,
    isIP = false,
  }: {
    serverData: ServerData;
    isIP?: boolean;
  }) => {
    const { server, blacklists } = serverData;

    const sortedBlacklists = React.useMemo(
      () => sortBlacklists(blacklists),
      [blacklists]
    );
    const listedCount = React.useMemo(
      () => getListedCount(blacklists),
      [blacklists]
    );

    const isClean = listedCount === 0;
    const key = `${isIP ? "ip" : "domain"}-${server}`;

    return (
      <Collapsible key={key}>
        <CollapsibleTrigger className="w-full group/row">
          <div className="flex items-center justify-between p-4 hover:bg-muted/50">
            <div className="flex items-center gap-2">
              {isClean ? (
                <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
              ) : (
                <XCircle className="w-4 h-4 text-red-500 shrink-0" />
              )}
              <span className="text-sm font-medium">{server}</span>
            </div>
            <div className="flex items-center gap-2">
              <Badge
                variant="outline"
                className={
                  isClean
                    ? "bg-green-500/10 text-green-500 border-none"
                    : "bg-red-500/10 text-red-500 border-none"
                }
              >
                {isClean ? "Clean" : `${listedCount} Listed`}
              </Badge>
              <ChevronDown className="w-4 h-4 text-muted-foreground transition-transform duration-200 group-data-[state=open]/row:rotate-180" />
            </div>
          </div>
        </CollapsibleTrigger>
        <CollapsibleContent className="border-t pl-3">
          <BlackListTable
            blacklists={sortedBlacklists}
            className="border-l"
          />
        </CollapsibleContent>
      </Collapsible>
    );
  }
);

const ServerSection = React.memo(
  ({
    title,
    servers,
    isIP = false,
  }: {
    title: string;
    servers: ServerData[];
    isIP?: boolean;
  }) => (
    <div className={isIP ? "mb-2" : ""}>
      <div className="py-2 px-4 bg-muted font-medium text-sm">{title}</div>
      <div className="divide-y">
        {servers.map((serverData) => (
          <ServerItem
            key={serverData.server}
            serverData={serverData}
            isIP={isIP}
          />
        ))}
      </div>
    </div>
  )
);

const BlackList = React.memo(({ type, data }: BlackListProps) => {
  const config = BLACKLIST_CONFIG[type];

  const { ipServers, domainServers } = React.useMemo(() => {
    if (!data) return { ipServers: [], domainServers: [] };

    return data.reduce(
      (acc, item) => {
        if (isIPAddress(item.server)) {
          acc.ipServers.push(item);
        } else {
          acc.domainServers.push(item);
        }
        return acc;
      },
      { ipServers: [] as ServerData[], domainServers: [] as ServerData[] }
    );
  }, [data]);

  const hasIPServers = ipServers.length > 0;
  const hasDomainServers = domainServers.length > 0;

  return (
    <Collapsible defaultOpen>
      <Card className="overflow-hidden">
        <CollapsibleTrigger className="w-full">
          <BlackListHeader config={config} />
        </CollapsibleTrigger>
        <CollapsibleContent>
          <CardContent className="p-0">
            <div className="rounded-lg">
              {hasIPServers && (
                <ServerSection
                  title="IP Addresses (A Records)"
                  servers={ipServers}
                  isIP={true}
                />
              )}
              {hasDomainServers && (
                <ServerSection
                  title="Host Names"
                  servers={domainServers}
                  isIP={false}
                />
              )}
            </div>
          </CardContent>
        </CollapsibleContent>
      </Card>
    </Collapsible>
  );
});

const BlackListTable = React.memo(
  ({
    blacklists,
    className,
  }: {
    blacklists: Pick<BlackListData, "rblName" | "listed" | "response">[];
    className?: string;
  }) => {
    return (
      <Table className={cn("w-full text-sm", className)}>
        <TableHeader>
          <TableRow className="border-b bg-muted/50">
            <TableHead className="py-2 px-4 text-left font-medium hidden md:table-cell">
              Blacklist
            </TableHead>
            <TableHead className="py-2 px-4 text-left font-medium hidden md:table-cell">
              Status
            </TableHead>
            <TableHead className="py-2 px-4 text-left font-medium hidden md:table-cell">
              Response
            </TableHead>
          </TableRow>
        </TableHeader>
        <TableBody className="divide-y">
          {blacklists?.map((blacklist, idx) => {
            const yellowListed = isYellowListed(blacklist);

            return (
              <TableRow key={idx}>
                <TableCell className="py-2 px-4">
                  <div className="items-center gap-2 hidden md:flex">
                    {yellowListed ? (
                      <CircleAlert className="w-4 h-4 text-yellow-500 shrink-0" />
                    ) : blacklist.listed ? (
                      <XCircle className="w-4 h-4 text-red-500 shrink-0" />
                    ) : (
                      <CheckCircle2 className="w-4 h-4 text-green-500 shrink-0" />
                    )}
                    <span className="text-muted-foreground break-all">
                      {blacklist.rblName}
                    </span>
                  </div>

                  <div className="md:hidden space-y-1.5">
                    <div className="flex gap-2">
                      <span className=" text-muted-foreground min-w-[80px]">
                        Blacklist:
                      </span>
                      <span className="text-wrap break-all">
                        {blacklist.rblName}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className=" text-muted-foreground min-w-[80px]">
                        Status:
                      </span>
                      <span
                        className={cn(
                          "flex items-center gap-1",
                          blacklist.listed
                            ? "text-red-500 bg-red-500/10 px-1.5 py-0.5 rounded-md"
                            : "text-green-500 bg-green-500/10 px-1.5 py-0.5 rounded-md"
                        )}
                      >
                        {blacklist.listed ? "Listed" : "Clean"}
                      </span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className=" text-muted-foreground min-w-[80px]">
                        Response:
                      </span>
                      <span className="text-wrap break-all">
                        {blacklist.response || "No response"}
                      </span>
                    </div>
                  </div>
                </TableCell>
                <TableCell className="py-2 px-4 text-muted-foreground hidden md:table-cell">
                  {blacklist.listed ? "Listed" : "Clean"}
                </TableCell>
                <TableCell className="py-2 px-4 text-muted-foreground hidden md:table-cell text-wrap break-words break-all">
                  {blacklist.response || "No response"}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    );
  }
);

BlackList.displayName = "BlackList";
BlackListTable.displayName = "BlackListTable";
ServerSection.displayName = "ServerSection";
ServerItem.displayName = "ServerItem";
BlackListHeader.displayName = "BlackListHeader";

export { BlackListTable };
export default BlackList;
